<template>
  <Teleport to="body">
    <div class="toast-container" :class="`toast-container--${position}`">
      <TransitionGroup name="toast" tag="div">
        <ToastNotification
          v-for="toast in regularToasts"
          :key="toast.id"
          :message="toast.message"
          :type="toast.type"
          :persistent="toast.persistent"
          :action="toast.action"
          @dismiss="dismissToast(toast.id)"
        />
      </TransitionGroup>
    </div>

    <!-- Overlay toasts: centered with backdrop -->
    <Transition name="overlay">
      <div v-if="overlayToasts.length" class="toast-overlay" @click.self="dismissToast(overlayToasts[0].id)">
        <ToastNotification
          v-for="toast in overlayToasts"
          :key="toast.id"
          :message="toast.message"
          :type="toast.type"
          :persistent="toast.persistent"
          :action="toast.action"
          @dismiss="dismissToast(toast.id)"
        />
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { ref, computed, onUnmounted } from 'vue'
import ToastNotification from './ToastNotification.vue'

const props = defineProps({
  position: {
    type: String,
    default: 'top-center',
    validator: (value) => ['top-left', 'top-center', 'top-right', 'bottom-left', 'bottom-center', 'bottom-right'].includes(value)
  },
  maxToasts: {
    type: Number,
    default: 5
  }
})

const toasts = ref([])
const timers = new Map()

const regularToasts = computed(() => toasts.value.filter(t => !t.overlay))
const overlayToasts = computed(() => toasts.value.filter(t => t.overlay))

// Generate unique ID for each toast
const generateId = () => `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`

// Add a new toast
const addToast = (message, options = {}) => {
  const id = generateId()
  const toast = {
    id,
    message,
    type: options.type || 'info',
    duration: options.duration ?? (options.type === 'error' || options.type === 'danger' ? 5000 : 3000),
    persistent: options.persistent || false,
    action: options.action || null,
    overlay: options.overlay || false
  }

  // Limit number of toasts
  if (toasts.value.length >= props.maxToasts) {
    const oldestToast = toasts.value[0]
    dismissToast(oldestToast.id)
  }

  toasts.value.push(toast)

  // Auto-dismiss non-persistent toasts
  if (!toast.persistent && toast.duration > 0) {
    const timer = setTimeout(() => {
      dismissToast(id)
    }, toast.duration)
    timers.set(id, timer)
  }

  return id
}

// Dismiss a toast
const dismissToast = (id) => {
  const index = toasts.value.findIndex(t => t.id === id)
  if (index > -1) {
    toasts.value.splice(index, 1)
    
    // Clear timer if exists
    if (timers.has(id)) {
      clearTimeout(timers.get(id))
      timers.delete(id)
    }
  }
}

// Clear all toasts
const clearAll = () => {
  // Clear all timers
  timers.forEach(timer => clearTimeout(timer))
  timers.clear()
  
  // Clear all toasts
  toasts.value = []
}

// Expose methods for parent component
defineExpose({
  addToast,
  dismissToast,
  clearAll,
  toasts
})

// Cleanup on unmount
onUnmounted(() => {
  timers.forEach(timer => clearTimeout(timer))
  timers.clear()
})
</script>

<style lang="scss" scoped>
.toast-container {
  position: fixed;
  z-index: 10010;
  pointer-events: none;
  padding: 1rem;

  // Position variants
  &--top-left {
    top: 0;
    left: 0;
  }

  &--top-center {
    top: 0;
    left: 50%;
    transform: translateX(-50%);
  }

  &--top-right {
    top: 0;
    right: 0;
  }

  &--bottom-left {
    bottom: 0;
    left: 0;
  }

  &--bottom-center {
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
  }

  &--bottom-right {
    bottom: 0;
    right: 0;
  }

  // Allow clicks on toasts
  :deep(.toast-notification) {
    pointer-events: auto;
  }
}

// Toast animations
.toast-enter-active,
.toast-leave-active {
  transition: all 0.3s ease;
}

.toast-enter-from {
  transform: translateY(-100%);
  opacity: 0;
}

.toast-leave-to {
  transform: translateX(100%);
  opacity: 0;
}

.toast-move {
  transition: transform 0.3s ease;
}

// Overlay backdrop + centered toast
.toast-overlay {
  position: fixed;
  inset: 0;
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.45);

  :deep(.toast-notification) {
    pointer-events: auto;
  }
}

.overlay-enter-active,
.overlay-leave-active {
  transition: opacity 0.3s ease;
}

.overlay-enter-from,
.overlay-leave-to {
  opacity: 0;
}
</style>